/**
 * Lawn Art's pictures (owner, 2026-10-05: recognizable pictures instead of abstract bars). Drawn in
 * two tones: `L` light, mowed up and down (along), `D` dark, mowed side to side (across); `.` is
 * the show lawn round it (nothing to mow). Only the picture is mowed, so it appears as it is cut.
 *
 * Every light stretch running up and down must reach the show lawn at one end at least, so it can
 * be entered straight (the dark parts are painted first, and the light ones put right whatever the
 * way into them crossed). artPictures.test checks it.
 */
export const ART_PICTURES = {
  flower: [
    '...LLL...',
    '..LLLLL..',
    '.LLDDDLL.',
    '.LLDDDLL.',
    '..LLLLL..',
    '...LLL...',
    '....L....',
    '.DD.L.DD.',
    '..DDLDD..',
    '....L....',
    '....L....',
  ],
  heart: [
    '.LL...LL.',
    'LLLL.LLLL',
    'LLLLLLLLL',
    'LLDLLLLLL',
    '.LLLLLLL.',
    '..LLLLL..',
    '...LLL...',
    '....L....',
  ],
  sun: [
    '....L....',
    '....L....',
    '.L.....L.',
    '..LDDDL..',
    '..DDDDD..',
    'D.DDDDD.D',
    '..DDDDD..',
    '..LDDDL..',
    '.L.....L.',
    '....L....',
    '....L....',
  ],
  star: [
    '....L....',
    '....L....',
    '...LLL...',
    'LLLLLLLLL',
    '.LLLLLLL.',
    '..LLLLL..',
    '..LL.LL..',
    '.LL...LL.',
    '.L.....L.',
  ],
  house: [
    '....L....',
    '...LLL...',
    '..LLLLL..',
    '.LLLLLLL.',
    'LLLLLLLLL',
    '.DDDDDDD.',
    '.D.DDD.D.',
    '.DDDLDDD.',
    '.DDDLDDD.',
  ],
  tree: [
    '...LLL...',
    '..LLLLL..',
    '.LLLLLLL.',
    '.LLLLLLL.',
    '.LLLLLLL.',
    '..LLLLL..',
    '...LLL...',
    '....D....',
    '....D....',
    '...DDD...',
  ],
  fish: ['..LLLL...L', '.LLLLLL.LL', 'LLDLLLLLLL', '.LLLLLL.LL', '..LLLL...L'],
  mower: [
    'L.........',
    '.L........',
    '..L.......',
    '...L......',
    '...DDDDDD.',
    '..DDDDDDDD',
    '..DDDDDDDD',
    '..L.L..L.L',
  ],
} as const satisfies Record<string, readonly string[]>;

export type ArtPicture = keyof typeof ART_PICTURES;
export const ART_PICTURE_NAMES = Object.keys(ART_PICTURES) as ArtPicture[];

/**
 * The picture's tiles as the lawn holds them ('|' along, '-' across, '.' none), centered on a
 * `w` x `h` canvas (a little above the middle).
 */
export function pictureOnCanvas(picture: ArtPicture, w: number, h: number): string[] {
  const rows = ART_PICTURES[picture];
  const pw = rows[0].length;
  const ph = rows.length;
  if (pw + 2 > w || ph + 2 > h) throw new Error(`${picture} does not fit a ${w}x${h} canvas`);
  const left = Math.floor((w - pw) / 2);
  const top = Math.max(1, Math.floor((h - ph) / 2) - 1);
  const out: string[] = [];
  for (let y = 0; y < h; y++) {
    let row = '';
    for (let x = 0; x < w; x++) {
      const c = rows[y - top]?.[x - left];
      row += c === 'L' ? '|' : c === 'D' ? '-' : '.';
    }
    out.push(row);
  }
  return out;
}

/**
 * Whether every light (along) stretch of a picture can be entered straight from the show lawn at
 * one end at least (see the note at the top).
 */
export function paintable(pattern: readonly string[]): boolean {
  const h = pattern.length;
  const w = pattern[0]?.length ?? 0;
  const at = (x: number, y: number) => (y < 0 || y >= h ? '.' : (pattern[y]?.[x] ?? '.'));
  for (let x = 0; x < w; x++) {
    for (let y = 0; y < h; y++) {
      if (at(x, y) !== '|' || at(x, y - 1) === '|') continue;
      let end = y;
      while (at(x, end + 1) === '|') end++;
      if (at(x, y - 1) !== '.' && at(x, end + 1) !== '.') return false;
      y = end;
    }
  }
  return true;
}
